import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';

import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';

import { handleApiRequest } from '../../api';
import useApiError from '../../hook/useApiError';
import useGlobalStatus from '../../hook/useGlobalStatus';

import Loading from '../../components/loading/Loading';
import AlertMsg from '../../components/alert/AlertMsg';
import ModalComponent from '../../components/modal/ModalComponent';

import { SUCCESS_CPOY } from '../../constants/alertMsg';

import theme from '../../theme';

import GenerationForm from './generationForm/index';
import GptMessage from './chatbot/index';
import EnterMessage from './EnterMessage';
import { TabStack, TabTitle } from './marketingWordStyle';
import { PROTUCT_TYPE, DEFAULT_INPUT_MESSAGE, LOADING_MSG, CUSTOM_IDENTITY, validateForm } from './constants';

const MarketingWord = (props) => {
  const location = useLocation();
  const { state } = location;
  const { setIsModalOpen, handleAlertMsgSettings, alertMsgData } = useGlobalStatus();
  const { handleApiErrorStatus } = useApiError();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(false);
  const [inputErrorMsg, setInputErrorMsg] = useState(null);
  const [productType, setProductType] = useState("");
  const [generationForm, setGenerationForm] = useState({});
  const [chatData, setChatData] = useState([]);
  const [currentMsg, setCurrentMsg] = useState(DEFAULT_INPUT_MESSAGE);
  const [currentTab, setCurrentTab] = useState(0);
  const gptBoxRef = useRef(null);
  const isMoblie = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (state?.identity) {
      setProductType(PROTUCT_TYPE.SELECT);
    }
  }, [state])

  const getIdentity = () => {
    if (productType === PROTUCT_TYPE.CUSTOM && generationForm?.generation_product_title) {
      return 'custom';
    }

    if (productType === PROTUCT_TYPE.CUSTOM && !generationForm?.generation_product_title) {
      return;
    }

    return state?.identity;
  }

  const getGenerationText = () => {
    setInputErrorMsg(null);
    if (!productType) return;

    const identity = getIdentity();
    if (!identity) return;

    setIsLoading(true);

    const params = new URLSearchParams({
      generation_product_identity: identity,
    })

    handleApiRequest('getGenerationText', params)
      .then(res => {
        if (res.status === 200) {
          const resultData = res.data.result;
          setGenerationForm(resultData);
          setChatData(resultData.messages);
          setIsLoading(false);
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  useEffect(() => {
    getGenerationText();
    if (productType === PROTUCT_TYPE.CUSTOM) {
      setChatData([]);
    }
  }, [productType])

  useEffect(() => {
    if (alertMsgData?.msg === SUCCESS_CPOY) {
      handleAlertMsgSettings(false, null);
    }
  }, [])

  const handleChatData = (message) => {
    const { newErrorMsg, isValid } = validateForm(productType, generationForm);
    setInputErrorMsg(newErrorMsg);

    if (!isValid) return;

    scrollToBottom();

    const newMsg = message ? message : DEFAULT_INPUT_MESSAGE;
    setCurrentMsg(newMsg);

    const newData = {
      role: "user",
      text: newMsg
    }

    const loadingData = {
      role: "model",
      text: "......"
    }

    const updatedChatData = [...chatData, newData];
    const loadingChatData = [...chatData, newData, loadingData];
    setChatData(loadingChatData);

    putGenerationText(updatedChatData);
  }

  const putGenerationText = (chatData) => {
    setIsModalOpen(false);
    if (!productType || !generationForm) return;
    setProgress(true);
    setCurrentMsg('');

    let requestData;

    const selectId = new URLSearchParams({
      identity: generationForm.identity
    })

    const customID = new URLSearchParams({
      identity: CUSTOM_IDENTITY
    })

    if (productType === PROTUCT_TYPE.SELECT) {
      requestData = {
        params: selectId,
        body: {
          generation_text_objective_type: generationForm.generation_text_objective_type,
          generation_text_objective_custom: generationForm.generation_text_objective_custom,
          messages: chatData
        }
      }
    } else if (productType === PROTUCT_TYPE.CUSTOM) {
      requestData = {
        params: customID,
        body: {
          generation_product_title: generationForm.generation_product_title,
          generation_text_objective_type: generationForm.generation_text_objective_type,
          generation_text_objective_custom: generationForm.generation_text_objective_custom,
          messages: chatData
        }
      }
    } else {
      return;
    }

    handleApiRequest('putGenerationText', requestData)
      .then(res => {
        if (res.status === 200) {
          const resultData = res.data.result;
          setGenerationForm(resultData);
          setChatData(resultData.messages);
          setProgress(false);
        } else {
          handleApiErrorStatus(res);
        }
      })
  }

  const scrollToBottom = () => {
    const gptBox = gptBoxRef.current;
    if (!gptBox) return;
    // 設置 scrollTop 為當前滾動高度 scrollHeight，確保保持在底部
    gptBox.scrollTop = gptBox.scrollHeight;
  }

  const handleClearFormSettings = () => {
    setProductType("");
    setGenerationForm({});
    setChatData([]);
    setIsModalOpen(false);
  }

  const handleModalSubmit = (modalData, type) => {
    switch (type) {
      case 'deleteChatList':
        putGenerationText([]);
        break;
      case 'clearFormSettings':
        handleClearFormSettings();
        break;
      default:
        break;
    }
  }

  return (
    <>
      <Box mt={isMoblie ? 2 : 4}>
        {!isMoblie &&
          <Box
            sx={{
              display: { sm: 'block', md: 'flex' },
              position: 'relative',
              height: '86vh',
            }}
          >
            <GenerationForm
              productType={productType}
              setProductType={setProductType}
              generationForm={generationForm}
              setGenerationForm={setGenerationForm}
              inputErrorMsg={inputErrorMsg}
              progress={progress}
            />

            <GptMessage
              chatData={chatData}
              progress={progress}
              scrollToBottom={scrollToBottom}
              gptBoxRef={gptBoxRef}
            />
          </Box>
        }
        {isMoblie &&
          <>
            <TabStack variant='center-center'>
              <TabTitle
                currenttab={currentTab}
                color={theme.palette.primary.main}
                type={'generationForm'}
                onClick={() => setCurrentTab(0)}
              >
                商品設定
              </TabTitle>
              <TabTitle
                currenttab={currentTab}
                color={theme.palette.primary.main}
                type={'gptMessage'}
                onClick={() => { setCurrentTab(1) }}
              >
                AI助手
              </TabTitle>
            </TabStack>

            <Box sx={{ overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'relative',
                  top: 0,
                  left: `-${currentTab * 100}%`,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '200%',
                  transition: 'left .5s',
                }}
              >
                <Box id="generationForm" sx={{ width: '100%' }}>
                  <GenerationForm
                    productType={productType}
                    setProductType={setProductType}
                    generationForm={generationForm}
                    setGenerationForm={setGenerationForm}
                    inputErrorMsg={inputErrorMsg}
                    progress={progress}
                  />
                </Box>

                <Box id="gptMessage" sx={{ width: '100%' }}>
                  <GptMessage
                    chatData={chatData}
                    progress={progress}
                    scrollToBottom={scrollToBottom}
                    gptBoxRef={gptBoxRef}
                  />
                </Box>
              </Box>
            </Box>
          </>
        }
        <EnterMessage
          currentMsg={currentMsg}
          setCurrentMsg={setCurrentMsg}
          handleChatData={handleChatData}
          handleModalSubmit={handleModalSubmit}
          progress={progress}
          productType={productType}
          isMoblie={isMoblie}
          setCurrentTab={setCurrentTab}
        />
      </Box>

      <ModalComponent
        isLoading={progress}
        handleModalSubmit={handleModalSubmit}
      />

      <AlertMsg />

      <Loading
        isLoading={isLoading}
        content={LOADING_MSG}
      />
    </>
  )
}

export default MarketingWord;